import { Navigate, Outlet } from "react-router-dom";
import { requireRoninAdmin } from "../auth/RequireAuth";
import { Breadcrumb } from "../../components/Breadcrumb";
import { ErrorBoundary } from "../../components/ErrorBoundary";
import ReportsNewLanding from "./ReportsNewLanding";
import EventSummaryReport from "./pages/EventSummaryReport";
import FinancialByDayReport from "./pages/FinancialByDayReport";
import ProductMixReport from "./pages/ProductMixReport";
import SalesByDayReport from "./pages/SalesByDayReport";
import TransactionAuditReport from "./pages/TransactionAuditReport";
import ReconciliationReport from "./pages/ReconciliationReport";
import FinancialReport from "./pages/FinancialReport";
import TicketingFinancialReport from "./pages/TicketingFinancialReport";
import NetHourlyReport from "./pages/NetHourlyReport";
import TicketingNetHourlyReport from "./pages/TicketingNetHourlyReport";
import VerticalSalesReport from "./pages/VerticalSalesReport";
import DiscountUsageReport from "./pages/DiscountUsageReport";
import EventSummaryDashboard from "./pages/EventSummaryDashboard";
import TransactionReport from "./pages/TransactionReport";
import OrderItemsReport from "./pages/OrderItemsReport";
import TabManagementReport from "./pages/TabManagementReport";
import WebCheckoutReport from "./pages/WebCheckoutReport";
import CommissaryReport from "./pages/CommissaryReport";

// Parallel new-reporting surface. Lives alongside /reports (which is untouched).
// RONIN-INTERNAL ONLY while in QA: gated by requireRoninAdmin() (user.role_id === 1) — the SAME
// guard the reconciliation tool (/reports/mx-merchant-reconciliation) and the other Ronin-only
// reports use. This is the actual staff role, NOT the "admin" permission every customer-admin
// login receives, so customers and customer-admins can't reach these pages. The guard sits on
// this PARENT route, so it runs for the index AND every child on direct URL navigation/reload —
// a non-internal user is redirected to "/" before any child renders.
const ReportsNewIndex = () => (
  <div className="min-h-screen px-4">
    <Outlet />
  </div>
);

export const reportsNewRouter = {
  path: "/reports-new",
  id: "reports-new",
  element: <ReportsNewIndex />,
  loader: requireRoninAdmin(),
  errorElement: <ErrorBoundary />,
  handle: {
    crumb: () => <Breadcrumb name="New Reporting" href="/reports-new" />,
  },
  children: [
    {
      index: true,
      element: <ReportsNewLanding />,
    },
    {
      path: "financial",
      id: "reports-new-financial",
      element: <FinancialReport />,
      handle: {
        crumb: () => (
          <Breadcrumb name="Financial" href="/reports-new/financial" />
        ),
      },
    },
    {
      path: "ticketing-financial",
      id: "reports-new-ticketing-financial",
      element: <TicketingFinancialReport />,
      handle: {
        crumb: () => (
          <Breadcrumb
            name="Ticketing Financial"
            href="/reports-new/ticketing-financial"
          />
        ),
      },
    },
    {
      path: "financial-summary",
      id: "reports-new-financial-summary",
      element: <EventSummaryReport />,
      handle: {
        crumb: () => (
          <Breadcrumb
            name="Financial Summary by Event"
            href="/reports-new/financial-summary"
          />
        ),
      },
    },
    {
      path: "financial-by-day",
      id: "reports-new-financial-by-day",
      element: <FinancialByDayReport />,
      handle: {
        crumb: () => (
          <Breadcrumb
            name="Financial Summary by Day"
            href="/reports-new/financial-by-day"
          />
        ),
      },
    },
    {
      path: "sales-by-day",
      id: "reports-new-sales-by-day",
      element: <SalesByDayReport />,
      handle: {
        crumb: () => (
          <Breadcrumb name="Sales by Day" href="/reports-new/sales-by-day" />
        ),
      },
    },
    {
      path: "product-mix",
      id: "reports-new-product-mix",
      element: <ProductMixReport />,
      handle: {
        crumb: () => (
          <Breadcrumb name="Product Mix" href="/reports-new/product-mix" />
        ),
      },
    },
    {
      // RETIRED (RP-2155): this was a 2-column SalesByVendor stub (OrderCount + Gross) advertised
      // on the landing page as the replacement for the legacy ~28-column Vendor Sales Report — a QA
      // following that card opened the wrong report and failed the ticket. The real replacement is
      // Financial grouped by Vendor, so the old URL now redirects there with the group-by preset.
      path: "sales-by-vendor",
      id: "reports-new-sales-by-vendor",
      element: <Navigate to="/reports-new/financial?dimension=Vendor" replace />,
    },
    {
      path: "transaction-audit",
      id: "reports-new-transaction-audit",
      element: <TransactionAuditReport />,
      handle: {
        crumb: () => (
          <Breadcrumb
            name="Transaction Audit"
            href="/reports-new/transaction-audit"
          />
        ),
      },
    },
    {
      path: "reconciliation",
      id: "reports-new-reconciliation",
      element: <ReconciliationReport />,
      handle: {
        crumb: () => (
          <Breadcrumb name="Reconciliation" href="/reports-new/reconciliation" />
        ),
      },
    },
    {
      path: "net-hourly",
      id: "reports-new-net-hourly",
      element: <NetHourlyReport />,
      handle: {
        crumb: () => (
          <Breadcrumb name="Net Hourly" href="/reports-new/net-hourly" />
        ),
      },
    },
    {
      path: "ticketing-net-hourly",
      id: "reports-new-ticketing-net-hourly",
      element: <TicketingNetHourlyReport />,
      handle: {
        crumb: () => (
          <Breadcrumb
            name="Ticketing Net Hourly"
            href="/reports-new/ticketing-net-hourly"
          />
        ),
      },
    },
    {
      path: "vertical-sales",
      id: "reports-new-vertical-sales",
      element: <VerticalSalesReport />,
      handle: {
        crumb: () => (
          <Breadcrumb name="Vertical Sales" href="/reports-new/vertical-sales" />
        ),
      },
    },
    {
      path: "discounts",
      id: "reports-new-discounts",
      element: <DiscountUsageReport />,
      handle: {
        crumb: () => (
          <Breadcrumb name="Discounts & Promo Codes" href="/reports-new/discounts" />
        ),
      },
    },
    {
      path: "event-summary",
      id: "reports-new-event-summary",
      element: <EventSummaryDashboard />,
      handle: {
        crumb: () => (
          <Breadcrumb name="Event Summary" href="/reports-new/event-summary" />
        ),
      },
    },
    {
      path: "transactions",
      id: "reports-new-transactions",
      element: <TransactionReport />,
      handle: {
        crumb: () => (
          <Breadcrumb name="Transactions" href="/reports-new/transactions" />
        ),
      },
    },
    {
      path: "order-items",
      id: "reports-new-order-items",
      element: <OrderItemsReport />,
      handle: {
        crumb: () => (
          <Breadcrumb name="Order Items" href="/reports-new/order-items" />
        ),
      },
    },
    {
      path: "tab-management",
      id: "reports-new-tab-management",
      element: <TabManagementReport />,
      handle: {
        crumb: () => (
          <Breadcrumb name="Tab Management" href="/reports-new/tab-management" />
        ),
      },
    },
    {
      path: "web-checkout",
      id: "reports-new-web-checkout",
      element: <WebCheckoutReport />,
      handle: {
        crumb: () => (
          <Breadcrumb name="Web Checkout" href="/reports-new/web-checkout" />
        ),
      },
    },
    {
      path: "commissary",
      id: "reports-new-commissary",
      element: <CommissaryReport />,
      handle: {
        crumb: () => (
          <Breadcrumb name="Commissary" href="/reports-new/commissary" />
        ),
      },
    },
  ],
};
